/**
 * This file controls the workflow when using the text mode interface for flexi.
 */
import {computed, getCurrentInstance, onMounted, ref} from "vue";
import {
    clearActiveTextModeContext,
    trackAssessmentCompleted,
    trackNoteResubmitRequested, trackNoteSubmitted,
    trackQuestionAnswered,
    trackQuestionsSubmitted,
    trackRhsEditStarted,
    trackRhsEditSubmitted,
    trackTextModeAlertActionClicked,
    trackTextModeAlertShown,
    trackTextModeComprehensiveEscalationPrompted,
    trackTextModeNetworkErrorRetryClicked,
    trackTextModeNetworkErrorShown,
    trackTextModeStarted
} from "../analytics/text-mode-analytics";
import {
    setStreamlinedOptions,
    setTrackByPatientType,
    submitTextModeNote,
    submitTextModeComprehensiveContinuation,
    submitTextModeObservationEdits, submitTextModeQnaAnswers
} from "@/features/text-mode/services/text-mode-submit.service";
import {ASSESSMENT_MODE_ROUTE_LOCATIONS} from "@/features/assessment/config/assessment-mode.routes";
import {trackAssessmentModeSelected} from "@/features/assessment/analytics/assessment-mode-analytics";
import {revertEvidenceItem} from "@/features/text-mode/services/draft-evidence.service";
import {useNoteStore} from "@/features/text-mode/stores/note.store";
import {useQuestionStore} from "@/features/text-mode/stores/question.store";
import {useEvidenceStore} from "@/features/text-mode/stores/evidence.store";
import {useDraftStore} from "@/features/text-mode/stores/draft.store";
import {usePatientStore} from "@/features/text-mode/stores/patient.store";
import {useAlertStore} from "@/features/text-mode/stores/alert.store";
import {useLanguageNoticeStore} from "@/features/text-mode/stores/language-notice.store";
import {usePartnerConfigStore} from "@/stores/partnerConfig";
import {useEncounterStore} from "@/stores/encounter";
import {useLanguageStore} from "@/stores/language";
import { safePush } from '@/modules/navigation';
import {AuditEntry, ClinicalNoteState, EvidenceCategory, QuestionItem} from "@/features/text-mode/types/text-mode.types";
import {AssessmentMode} from "@/features/assessment/types/assessment-mode.types";
import { useTrackStore } from '@/stores/track';
import { useGuidedModeStore } from '@/features/guided-mode/stores/guidedMode';
import { buildGuidedHandoff } from '@/features/guided-mode/utils/guided-handoff';
import { isNetworkError } from '@/utils/networkError';
import { AssessmentConfigurationError } from '../api';
import { baseLocale } from '@/features/text-mode/utils/text-utils';

type TextModeSubmitActionType = 'note_submit' | 'question_submit' | 'rhs_submit';

export function useTextModeWorkflow() {
    const vm = getCurrentInstance();
    const proxy = vm ? vm.proxy : undefined;
    const router = proxy ? proxy.$router : undefined;

    const encounterStore = useEncounterStore();
    const isCompleting = ref(false);
    const startedAt = ref<number | null>(null);
    const firstSubmittedAt = ref<number | null>(null);
    const submissionIndex = ref(0);
    const rhsEditSubmitCount = ref(0);
    const questionSubmitCount = ref(0);
    const noteStore = useNoteStore();
    const questionStore = useQuestionStore();
    const evidenceStore = useEvidenceStore();
    const draftStore = useDraftStore();
    const patientStore = usePatientStore();
    const alertStore = useAlertStore();
    const trackStore = useTrackStore();
    const guidedModeStore = useGuidedModeStore();
    const languageStore = useLanguageStore();
    const languageNoticeStore = useLanguageNoticeStore();
    const partnerConfigStore = usePartnerConfigStore();
    const hasNetworkSubmitError = ref(false);
    const hasConfigurationSubmitError = ref(false);
    const hasUnresolvedConfigurationError = ref(false);
    const retryHandler = ref<(() => Promise<void>) | null>(null);
    const lastFailedActionType = ref<TextModeSubmitActionType | null>(null);

    const clinicalNote = computed<ClinicalNoteState>(() => noteStore.clinicalNote);
    const questions = computed<QuestionItem[]>(() => questionStore.questions);
    const questionStage = computed(() => questionStore.stage);
    const evidenceCategories = computed<EvidenceCategory[]>(() => evidenceStore.categories);
    const engagement = computed<string | undefined>(() => partnerConfigStore.code ?? undefined);
    const isNonEnglishLanguage = computed<boolean>(
        () => baseLocale(languageStore.selected) !== 'en',
    );
    const showLanguageAlert = computed<boolean>(
        () => isNonEnglishLanguage.value && !languageNoticeStore.acknowledged,
    );
    const showLanguageReminder = computed<boolean>(
        () => isNonEnglishLanguage.value && languageNoticeStore.acknowledged,
    );

    const canComplete = computed<boolean>(
        () => noteStore.clinicalNote.submitted
            && !hasConfigurationSubmitError.value
            && !hasUnresolvedConfigurationError.value
            && !hasNetworkSubmitError.value
            && !alertStore.hasAnyAlert
            && !alertStore.needsReEvaluation
            && !isCompleting.value
            && !draftStore.isEvidenceEditMode
            && !draftStore.hasDraftChanges
            && !draftStore.hasValidationErrors
            && questionStore.questions.length === 0
            && evidenceStore.categories.flatMap(cat => cat.items).length > 0,
    );

    const isFocusedAssessmentOpen = computed<boolean>(
        () => trackStore.initialAssessmentType === 'focused'
            && !trackStore.comprehensive,
    );

    const showFocusedCompletionPrompt = computed<boolean>(
        () => canComplete.value && isFocusedAssessmentOpen.value,
    );

    const showEvaluationErrorState = computed<boolean>(
        () => noteStore.clinicalNote.submitted && alertStore.hasAnyAlert,
    );


    const resetTextModeEncounterState = (): void => {
        hasConfigurationSubmitError.value = false;
        hasUnresolvedConfigurationError.value = false;
        noteStore.reset();
        noteStore.setFocusedAssessments([]);
        questionStore.reset();
        evidenceStore.reset();
        draftStore.fullReset();
        patientStore.reset();
        alertStore.reset();
    };

    const clearTextModeContext = (): void => {
        resetTextModeEncounterState();
        clearActiveTextModeContext();
    }

    const getAnalyticsContext = () => {
        const textModeStartedAt = (() => {
            if (startedAt.value === null) {
                return undefined;
            }

            const startedDate = new Date(startedAt.value);
            return Number.isNaN(startedDate.getTime())
                ? undefined
                : startedDate.toISOString();
        })();

        return {
            encounterId: encounterStore.uuid,
            routeName: proxy?.$route?.name ? String(proxy.$route.name) : 'TextMode',
            textModeStartedAt,
        };
    };

    const getAlertState = () => ({
        isComprehensive: alertStore.isComprehensive,
        isNotClinicalConcept: alertStore.isNotClinicalConcept,
        isNoFocusedLogic: alertStore.isNoFocusedLogic,
    });

    const trackResponseNotice = (
        triggerAction: TextModeSubmitActionType,
        submission?: { submissionIndex: number; submissionType: 'first_submit' | 'resubmit' },
    ): void => {
        const payload = {
            ...getAnalyticsContext(),
            alertState: getAlertState(),
            triggerAction,
            questions: questionStore.questions,
            evidenceCategories: evidenceStore.categories,
            ...submission,
        };

        if (alertStore.hasAnyAlert) {
            trackTextModeAlertShown(payload);
        }
        if (questionStore.showComprehensiveEscalationNotice) {
            trackTextModeComprehensiveEscalationPrompted(payload);
        }
    };

    const withNetworkErrorHandling = async (
        actionType: TextModeSubmitActionType,
        action: () => Promise<void>,
        retry: () => Promise<void>,
    ): Promise<boolean> => {
        hasNetworkSubmitError.value = false;
        hasConfigurationSubmitError.value = false;
        try {
            await action();
            hasUnresolvedConfigurationError.value = false;
            retryHandler.value = null;
            lastFailedActionType.value = null;
            return true;
        } catch (e) {
            if (e instanceof AssessmentConfigurationError) {
                hasConfigurationSubmitError.value = true;
                hasUnresolvedConfigurationError.value = true;
                retryHandler.value = retry;
                return false;
            }
            if (!isNetworkError(e)) {
                throw e;
            }

            hasNetworkSubmitError.value = true;
            retryHandler.value = retry;
            lastFailedActionType.value = actionType;
            trackTextModeNetworkErrorShown({ ...getAnalyticsContext(), actionType });
            return false;
        }
    };

    const retrySubmit = async (): Promise<void> => {
        if (!retryHandler.value) return;

        trackTextModeNetworkErrorRetryClicked({
            ...getAnalyticsContext(),
            actionType: lastFailedActionType.value ?? 'note_submit',
        });

        await retryHandler.value();
    };

    const dismissNetworkSubmitError = (): void => {
        hasNetworkSubmitError.value = false;
    };

    onMounted(() => {
        startedAt.value = Date.now();
        trackTextModeStarted(getAnalyticsContext());
    });

    const handleNoteInput = (value: string): void => {
        noteStore.setText(value);
    };

    const handleNoteSubmit = async (submittedText?: string): Promise<void> => {
        const nextSubmissionIndex = submissionIndex.value + 1;
        const submissionType = nextSubmissionIndex === 1 ? 'first_submit' : 'resubmit';

        if (nextSubmissionIndex === 1) {
            trackStore.resetAssessmentState();
        }

        const succeeded = await withNetworkErrorHandling(
            'note_submit',
            () => submitTextModeNote({
                encounterId: encounterStore.uuid,
                rawText: submittedText ?? noteStore.clinicalNote.text,
                engagement: engagement.value,
                noteStore,
                questionStore,
                evidenceStore,
                draftStore,
                patientStore,
                alertStore
            }),
            () => handleNoteSubmit(submittedText),
        );
        if (!succeeded) return;

        submissionIndex.value = nextSubmissionIndex;
        if (!firstSubmittedAt.value) {
            firstSubmittedAt.value = Date.now();
        }

        trackNoteSubmitted({
            ...getAnalyticsContext(),
            rawText: noteStore.clinicalNote.text,
            submissionIndex: nextSubmissionIndex,
            submissionType,
            questions: questionStore.questions,
            evidenceCategories: evidenceStore.categories,
            extractions: noteStore.originalExtractions,
            observations: noteStore.originalObservations,
            focusedAssessments: noteStore.focusedAssessments,
        });

        trackResponseNotice('note_submit', {
            submissionIndex: nextSubmissionIndex,
            submissionType,
        });

        if (patientStore.patient?.type) {
            /* Set the current clinical track before handling any blocking alert. */
            await setTrackByPatientType({
                tracks: trackStore.all,
                patientType: patientStore.patient?.type,
            });
        }

        if (!alertStore.hasAnyAlert && !trackStore.comprehensive) {
            /* set streamlined_options*/
            const filterConditions = trackStore.filterConditions;
            await setStreamlinedOptions({
                noteStore,
                filterConditions,
            })
        }

    };

    const handleQuestionToggle = (questionId: string): void => {
        questionStore.toggleQuestion(questionId);
    };

    const handleQuestionAnswer = (payload: { questionId: string; answer: string }): void => {
        const question = questionStore.questions.find((candidate) => candidate.id === payload.questionId) || null;
        questionStore.setQuestionAnswer(payload);
        trackQuestionAnswered({
            ...getAnalyticsContext(),
            questionRoundIndex: submissionIndex.value,
            question,
        });
    };

    const handleQuestionSubmit = async (): Promise<void> => {
        if (!questionStore.prepareQuestionsForSubmit()) return;
        questionStore.setHasBeenSubmitted(true);
        const submittedQuestions = questionStore.questions.map((question) => ({ ...question }));
        const stageBeforeSubmit = questionStore.stage;
        const succeeded = await withNetworkErrorHandling(
            'question_submit',
            () => submitTextModeQnaAnswers({
                encounterId: encounterStore.uuid,
                engagement: engagement.value,
                noteStore,
                questionStore,
                evidenceStore,
                draftStore,
                patientStore,
                alertStore
            }),
            handleQuestionSubmit,
        );
        if (!succeeded) return;

        questionStore.setLastSubmittedRound({ questions: submittedQuestions, stage: stageBeforeSubmit });
        questionSubmitCount.value += 1;
        trackQuestionsSubmitted({
            ...getAnalyticsContext(),
            questionRoundIndex: submissionIndex.value,
            questionSubmitCount: questionSubmitCount.value,
            questions: submittedQuestions,
            returnedQuestions: questionStore.questions,
            observations: noteStore.originalObservations,
            focusedAssessments: noteStore.focusedAssessments,
            comprehensive: trackStore.isComprehensiveScope,
        });

        trackResponseNotice('question_submit');

        await setTrackByPatientType({
            tracks: useTrackStore().all,
            patientType: patientStore.patient?.type,
        });
    };

    const handleFocusedContinueAssessment = async (): Promise<void> => {
        if (!isFocusedAssessmentOpen.value || !noteStore.clinicalNote.submitted) return;
        if (noteStore.clinicalNote.isSubmitting) return;

        const succeeded = await withNetworkErrorHandling(
            'question_submit',
            () => submitTextModeComprehensiveContinuation({
                encounterId: encounterStore.uuid,
                engagement: engagement.value,
                noteStore,
                questionStore,
                evidenceStore,
                draftStore,
                patientStore,
                alertStore
            }),
            handleFocusedContinueAssessment,
        );
        if (!succeeded) return;

        questionStore.setLastSubmittedRound(null);
        await setTrackByPatientType({
            tracks: trackStore.all,
            patientType: patientStore.patient?.type,
        });
    };

    const handleRevertEvidenceItem = (payload: { itemId: string }): void => {
        revertEvidenceItem(payload.itemId, draftStore, evidenceStore);
    };

    const handleRhsEditStarted = (): void => {
        trackRhsEditStarted(getAnalyticsContext());
    };

    const handleRhsSubmit = async (): Promise<void> => {
        if (!draftStore.prepareWarningsForSubmit()) return;

        let auditEntries: AuditEntry[] = [];
        const succeeded = await withNetworkErrorHandling(
            'rhs_submit',
            async () => {
                auditEntries = await submitTextModeObservationEdits({
                    encounterId: encounterStore.uuid,
                    engagement: engagement.value,
                    noteStore,
                    questionStore,
                    evidenceStore,
                    draftStore,
                    patientStore,
                    alertStore
                });
            },
            handleRhsSubmit,
        );
        if (!succeeded) return;

        rhsEditSubmitCount.value += 1;
        trackRhsEditSubmitted({
            ...getAnalyticsContext(),
            rhsEditSubmitCount: rhsEditSubmitCount.value,
            auditEntries,
            evidenceCategories: evidenceStore.categories,
        });
        trackResponseNotice('rhs_submit');
    };

    const handleModeChange = (mode: AssessmentMode): void => {
        if (router && mode !== 'text') {
            trackAssessmentModeSelected(mode, 'opened', 'TextMode.ClinicalNotePanel');
            router.push(ASSESSMENT_MODE_ROUTE_LOCATIONS[mode]).then();
        }
    };

    const handleLanguageNoticeContinue = (): void => {
        languageNoticeStore.acknowledge();
    };

    const handleLanguageNoticeSwitchToGuided = async (): Promise<void> => {
        languageNoticeStore.acknowledge();
        trackAssessmentModeSelected('guided', 'opened', 'TextMode.LanguageNotice');
        clearTextModeContext();
        await safePush(router, ASSESSMENT_MODE_ROUTE_LOCATIONS.guided);
    };

    const handleNoteResubmit = (): void => {
        trackNoteResubmitRequested({
            ...getAnalyticsContext(),
            nextSubmissionIndex: submissionIndex.value + 1,
        });

        noteStore.setOriginalExtractions([]);
        noteStore.setOriginalObservations([]);
        noteStore.setFocusedAssessments([]);
        questionStore.reset();
        evidenceStore.reset();
        draftStore.fullReset();
        patientStore.reset();
        alertStore.reset();
        trackStore.resetAssessmentState();
        questionSubmitCount.value = 0;
        rhsEditSubmitCount.value = 0;
        noteStore.setSubmitted(false);
    };

    const keepAnswersAndAwaitReEvaluation = (): void => {
        // NBQ-triggered alert: preserve observations, patient info, draft, and note submitted state
        alertStore.reset();
        alertStore.setNeedsReEvaluation(true);
        questionStore.resetQuestions();
    };

    const resetToNoteEditor = (): void => {
        // Initial-note-triggered alert: full reset back to text editor
        noteStore.setSubmitted(false);
        questionStore.reset();
        evidenceStore.reset();
        draftStore.fullReset();
        patientStore.reset();
        alertStore.reset();
        trackStore.resetAssessmentState();
    };

    const handleEmptyEvaluationBack = (): void => {
        const isNoFocusedLogic = alertStore.isNoFocusedLogic;
        trackTextModeAlertActionClicked({
            ...getAnalyticsContext(),
            alertState: getAlertState(),
            action: 'back',
        });

        if (questionStore.hasBeenSubmitted && !isNoFocusedLogic) {
            keepAnswersAndAwaitReEvaluation();
        } else {
            resetToNoteEditor();
        }
    };

    const handleFocusedBack = (): void => {
        if (!showFocusedCompletionPrompt.value) return;

        if (questionStore.restoreLastSubmittedRound(noteStore.originalObservations)) {
            alertStore.reset();
        } else if (questionStore.hasBeenSubmitted) {
            keepAnswersAndAwaitReEvaluation();
        } else {
            resetToNoteEditor();
        }
    };

    const handleNoFocusedContinue = async (): Promise<void> => {
        if (!alertStore.isNoFocusedLogic) return;

        trackTextModeAlertActionClicked({
            ...getAnalyticsContext(),
            alertState: getAlertState(),
            action: 'continue',
        });
        trackAssessmentModeSelected('guided', 'opened', 'TextMode.NoFocusedAlert');

        guidedModeStore.prepareComprehensiveHandoff(buildGuidedHandoff(
            trackStore.current?.path ?? patientStore.patient?.type,
            patientStore.patient,
        ));
        trackStore.startComprehensiveAssessment();
        clearTextModeContext();

        await safePush(router, ASSESSMENT_MODE_ROUTE_LOCATIONS.guided);
    };

    const handleComplete = async (): Promise<void> => {
        if (!canComplete.value) {
            return;
        }

        isCompleting.value = true;

        try {

            trackAssessmentCompleted({
                ...getAnalyticsContext(),
                startedAt: startedAt.value,
                firstSubmittedAt: firstSubmittedAt.value,
                submissionCount: submissionIndex.value,
                rhsEditSubmitCount: rhsEditSubmitCount.value,
            });

            await safePush(router, '/overall-assessment');
        } finally {
            isCompleting.value = false;
        }
    };

    return {
        clinicalNote,
        questions,
        questionStage,
        evidenceCategories,
        canComplete,
        clearTextModeContext,
        resetTextModeEncounterState,
        handleNoteInput,
        handleNoteSubmit,
        handleNoteResubmit,
        handleEmptyEvaluationBack,
        handleNoFocusedContinue,
        handleModeChange,
        handleLanguageNoticeContinue,
        handleLanguageNoticeSwitchToGuided,
        handleQuestionToggle,
        handleQuestionAnswer,
        handleQuestionSubmit,
        handleRevertEvidenceItem,
        handleRhsEditStarted,
        handleRhsSubmit,
        handleComplete,
        handleFocusedContinueAssessment,
        handleFocusedBack,
        showFocusedCompletionPrompt,
        showLanguageAlert,
        showLanguageReminder,
        showEvaluationErrorState,
        hasNetworkSubmitError,
        hasConfigurationSubmitError,
        dismissConfigurationSubmitError: () => { hasConfigurationSubmitError.value = false; },
        retrySubmit,
        dismissNetworkSubmitError,
    };
}
